/**
 * WalletPickerPanel.jsx — Panel PICKING : layout variante A (email-first + wallets compacts).
 *
 * Structure validée 2026-06-11 (dav2-wallet2-A.html) :
 *   - Eyebrow lead "Sign in or create your account"
 *   - PrimaryBtn "Continue with email" → EMAIL_LOGIN
 *   - OutlineAccentBtn "Create an account" → EMAIL_REGISTER
 *   - DividerOr "or connect a wallet"
 *   - WalletMiniRow (4 wallets en ligne compacte)
 *
 * Règles DA respectées :
 * §14 — Zéro import @mui/@material-ui.
 * §20 — Zéro littéral couleur en dur ; tout depuis les styles/theme.js.
 * §12 undecies — Casse grammaticale sur tous les textes UI.
 * §8.7 — Aucun import Redux.
 */

import React, { useState, useEffect } from 'react';
import { ACTION_TYPES }    from '../hooks/useWalletStateMachine';
import { resolveReferralCode } from '../../../services/ReferralService';
import { WALLET_CONNECTORS_META } from '../config/wagmiConfig';
import {
  PickerBody,
  WalletFooter,
  EyebrowLead,
  PrimaryBtn,
  OutlineAccentBtn,
  DividerOr,
  WalletMiniRow,
  WalletMiniBtn,
  WalletMiniIcon,
  WalletMiniLabel,
  WalletMiniInstallLink,
  ToSText,
} from '../styles/EarniWalletModal.styles';
import { Spinner } from '../../ui/ModalDA';
import { isMobile, getMobileDeepLink, getMobileStoreLink } from '../../../utils/functions/mobileWallet';


/* ─── Détection de la disponibilité du provider ─────────────────────────────── */

/**
 * Vérifie si un wallet est installé dans le navigateur.
 * Retourne toujours true côté SSR pour éviter le flash.
 *
 * @param {string} connectorId — id du connecteur wagmi
 * @returns {boolean}
 */
function isProviderAvailable(connectorId) {
  if (typeof window === 'undefined') return true;
  switch (connectorId) {
    case 'metaMask':       return !!(window.ethereum?.isMetaMask);
    case 'coinbaseWallet': return !!(window.coinbaseWalletExtension || window.ethereum?.isCoinbaseWallet);
    case 'trustWallet':    return !!(window.trustwallet || window.ethereum?.isTrust);
    case 'binanceWallet':  return !!(window.BinanceChain);
    default:               return true;
  }
}

/* ─── Composant principal ───────────────────────────────────────────────────── */

/**
 * WalletPickerPanel
 *
 * @param {object} props
 * @param {function} props.dispatch         — dispatch useWalletStateMachine
 * @param {function} props.connectWallet    — (connectorId) => Promise<void>
 * @param {boolean}  props.isConnecting     — wagmi isPending
 * @param {string[]} props.supportedWallets — IDs de connecteurs à afficher
 */
/* ─── Styles banner referral (réutilisation pattern EmailAuthPanel) ─────────── */
const referralBannerStyle = {
  display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
  background: 'rgba(194,24,166,0.08)', border: '1px solid rgba(194,24,166,0.25)',
  borderRadius: '0.5rem', padding: '0.625rem 0.75rem', marginBottom: '0.875rem',
  fontSize: '0.8125rem', lineHeight: '1.4', color: '#0d0a10',
};

export function WalletPickerPanel({ dispatch, connectWallet, connectTestWallet, isConnecting, supportedWallets, linkMode = false, contextBanner = null }) {
  const [referrerUsername, setReferrerUsername] = useState(null);

  // Detect ?ref= in URL and resolve the referral code to show the invitation banner
  useEffect(() => {
    const refCode = new URLSearchParams(window.location.search).get('ref');
    if (!refCode) return;
    resolveReferralCode(refCode)
      .then(data => { if (data && data.username) setReferrerUsername(data.username); })
      .catch(() => { /* invalid code — no banner */ });
  }, []);

  /* Filtrage des connecteurs selon les wallets supportés.
     Normalisation casse/séparateurs : un appelant qui passe ['metamask','coinbase',…]
     (lowercase) doit matcher les ids canoniques ['metaMask','coinbaseWallet',…].
     Sans ça → ligne vide (bug "Link a wallet"). */
  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
  const visibleConnectors = WALLET_CONNECTORS_META.filter((c) => {
    if (!supportedWallets || supportedWallets.length === 0) return true;
    return supportedWallets.some((w) => {
      const nw = norm(w);
      const nc = norm(c.id);
      return nc === nw || nc.startsWith(nw) || nw.startsWith(nc);
    });
  });

  /* ── Handlers ─────────────────────────────────────────────────────────────── */

  const handleWalletClick = (connectorId) => {
    dispatch({ type: ACTION_TYPES.SELECT_WALLET, payload: { walletId: connectorId } });
    connectWallet(connectorId, linkMode);
  };

  const handleSelectEmailLogin    = () => dispatch({ type: ACTION_TYPES.SELECT_EMAIL_LOGIN });
  const handleSelectEmailRegister = () => dispatch({ type: ACTION_TYPES.SELECT_EMAIL_REGISTER });

  /* ── Rendu (variante A — email-first, wallets compacts en bas) ──────────── */
  return (
    <>
      <PickerBody>
        {/* Banner contextuel (ex. dark mode) — message fourni par l'appelant via contextBanner */}
        {contextBanner && !linkMode && (
          <div style={referralBannerStyle}>
            <span style={{ fontSize: '1rem', flexShrink: 0 }}>{contextBanner.icon}</span>
            <span>{contextBanner.message}</span>
          </div>
        )}
        {/* Banner referral — visible dès l'ouverture si ?ref= dans l'URL */}
        {referrerUsername && !linkMode && (
          <div style={referralBannerStyle}>
            <span style={{ fontSize: '1rem', flexShrink: 0 }}>🎁</span>
            <span>
              <strong>@{referrerUsername}</strong> invited you to <strong>Earniverse</strong>, the AAA VR metaverse.
              Join <strong>Earnimarket</strong>, its collectibles marketplace, and you both earn{' '}
              <strong>50 earnicoins</strong>, up to <strong>150</strong> when you link Epic Games.
            </span>
          </div>
        )}

        {/* Eyebrow lead — masqué en linkMode (seuls les wallets ont du sens) */}
        {!linkMode && (
          <EyebrowLead>Sign in or create your account</EyebrowLead>
        )}

        {/* Boutons email — masqués en linkMode */}
        {!linkMode && (
          <>
            <PrimaryBtn
              type="button"
              onClick={handleSelectEmailLogin}
              aria-label="Continue with email"
            >
              Continue with email
            </PrimaryBtn>

            <OutlineAccentBtn
              type="button"
              onClick={handleSelectEmailRegister}
              aria-label="Create an account"
              style={{ marginTop: '0.7rem' }}
            >
              Create an account
            </OutlineAccentBtn>
          </>
        )}

        {/* Séparateur "or connect a wallet" */}
        <DividerOr>or connect a wallet</DividerOr>

        {/* Ligne de wallets compacts */}
        <WalletMiniRow aria-label="Choose a wallet">
          {visibleConnectors.map((connector) => {
            const available  = isProviderAvailable(connector.id);
            const onMobile   = isMobile();
            const deepLink   = onMobile && !available ? getMobileDeepLink(connector.id) : null;
            const storeLink  = onMobile && !available ? getMobileStoreLink(connector.id) : null;

            // Sur mobile sans injection : la carte devient un deep link vers l'app native
            // (metamask.app.link redirige vers le store si l'app n'est pas installée).
            // Sur desktop sans wallet : carte désactivée (comportement inchangé).
            const mobileAction = !!deepLink;
            const isDisabled   = isConnecting || (!available && !mobileAction);

            const handleClick = mobileAction
              ? () => { window.open(deepLink, '_blank', 'noopener,noreferrer'); }
              : () => { available && !isConnecting && handleWalletClick(connector.id); };

            return (
              <WalletMiniBtn
                key={connector.id}
                type="button"
                disabled={isDisabled}
                $mobileAction={mobileAction}
                aria-label={
                  mobileAction
                    ? `Open in ${connector.name}`
                    : available
                    ? `Connect ${connector.name}`
                    : `Install ${connector.name}`
                }
                title={
                  mobileAction
                    ? `Open ${connector.name} to connect`
                    : !available
                    ? `Install ${connector.name} to continue`
                    : undefined
                }
                onClick={handleClick}
              >
                <WalletMiniIcon
                  src={connector.icon}
                  alt={connector.name}
                  $unavailable={!available}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <WalletMiniLabel>{connector.name}</WalletMiniLabel>
                {!available && storeLink && (
                  <WalletMiniInstallLink
                    href={storeLink}
                    onClick={(e) => e.stopPropagation()}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Install ${connector.name}`}
                  >
                    Install
                  </WalletMiniInstallLink>
                )}
              </WalletMiniBtn>
            );
          })}
        </WalletMiniRow>
      </PickerBody>

      {/* Pied de modal — mention légale ToS */}
      <WalletFooter>
        <ToSText>
          By continuing, you agree to the{' '}
          <a
            href="https://earniverse.io/terms-of-service/"
            target="_blank"
            rel="noopener noreferrer"
          >
            terms of service
          </a>
          {' '}and the{' '}
          <a
            href="https://earniverse.io/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
          >
            privacy policy
          </a>
          .
        </ToSText>
      </WalletFooter>
    </>
  );
}

export default WalletPickerPanel;
